import {
  type GridColDef,
  type GridColumnGroupingModel,
  GridColumnHeaderTitle,
  type GridValueFormatter,
  type GridValueGetter,
} from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useUser } from '@/features/auth/hooks/useUser'
import { mediaSelectors } from '@/features/media/slice'
import { useAppSelector } from '@/redux/hooks'
import type {
  AffiliationsItem,
  PropertyItem,
  SearchResultItem,
} from '../../types'
import { ActionMenu } from './cells/ActionMenu'
import { BulkEditIndicatorCell } from './cells/BulkEditIndicatorCell'
import { CheckboxCell } from './cells/CheckboxCell'
import { ExpandableCell } from './cells/ExpandableCell'
import { StatusCell } from './cells/StatusCell'
import { ThumbnailCell } from './cells/ThumbnailCell'

//Extending the base grid column definition makes filling this out easier.
//Anything with dot accessors will work, but won't have autofill
type SearchColDef = GridColDef<SearchResultItem>

const getPerformanceAndRevisionNumber: GridValueGetter<SearchResultItem> = (
  items: { performanceNumber: string; revisionNumber: string }[],
) => {
  if (!items || !Array.isArray(items)) return []
  return items.map((i) => `${i.performanceNumber}-${i.revisionNumber}`)
}

const getPropertyItemDescription: GridValueGetter<SearchResultItem> = (
  items: PropertyItem[],
) => {
  if (!items || !Array.isArray(items)) return []
  return items.map((i) => i.description || i.code)
}

const getAffCodeLabel = (items: AffiliationsItem[]) => {
  if (!items || !Array.isArray(items)) return []
  return items.map((i) => `${i.code3} - ${i.description}`)
}

const formatStringArray: GridValueFormatter<SearchResultItem> = (
  items: PropertyItem[],
) => {
  if (!items) return ''
  return items.join(', ')
}

const getXMetadata: GridValueGetter<SearchResultItem> = (
  _value: never,
  row: SearchResultItem,
  column: GridColDef,
) => {
  if (!row.xMetadata || !row.xMetadata.length) return []

  const value = row.xMetadata
    .filter(
      (item) => item.dataCode.toLowerCase() === column.field.toLowerCase(),
    )
    .map((i) =>
      i.valueDescription === 'NoVal' ? i.freeTextValue : i.valueDescription,
    )

  return Array.from(new Set(value.flat()))
}

const formatDate: GridValueFormatter<SearchResultItem> = (date: Date) => {
  if (isNaN(date.valueOf())) {
    return ''
  }
  return date.toLocaleDateString(undefined, {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  })
}

const actionColumn: GridColDef[] = [
  {
    field: 'actions',
    headerName: 'Actions',
    headerAlign: 'left',
    type: 'actions',
    minWidth: 150,
    getActions: ActionMenu,
    hideable: false,
  },
]

//These columns only apply to Cat platform
export const catOnlyColumns: SearchColDef[] = [
  {
    headerName: 'Sub-Industries',
    field: 'subIndustries',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Configurable Sales Model',
    field: 'csm',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta CSM
  {
    headerName: 'Emission Tiers',
    field: 'emissionTiers',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Feature Code',
    field: 'fc',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta FC
  {
    headerName: 'Applications',
    field: 'applications',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Gas Engine Rating Pro(GERP)',
    field: 'gerp',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //x meta data - GERP?
  {
    headerName: 'CSQ',
    field: 'csq',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //x meta data CSQ
  {
    headerName: 'TIP Genset',
    field: 'tip',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //x meta data TIP
  {
    headerName: 'Electric Power Spec Sizer',
    field: 'eps',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta EPS
  {
    headerName: 'CAT.com Product Technical Information',
    field: 'pti',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta CAT
  {
    headerName: 'Power.Cat.Com',
    field: 'vsp',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, // xmeta VSP
]

// These options are only available to editors
export const providerRoleColumns: SearchColDef[] = [
  {
    headerName: 'Last Bulk Edit Modified By',
    field: 'lastBulkEditModifiedBy',
  },
  {
    headerName: 'Last Bulk Edit Date',
    field: 'lastBulkEditDate',
  },
  {
    headerName: 'Last Modified By ID',
    field: 'modifiedBy',
  },
  {
    headerName: 'Last Modified By Name',
    field: 'modifiedByName',
  },
  {
    headerName: 'Provider Name',
    field: 'provider/name',
    valueGetter: (_value, row) => row.provider.name,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Name'}
        />
      )
    },
  },
  {
    headerName: 'Provider Phone',
    field: 'provider/phone',
    valueGetter: (_value, row) => row.provider.phone,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Phone'}
        />
      )
    },
  },
  {
    headerName: 'Provider Email',
    field: 'provider/email',
    valueGetter: (_value, row) => row.provider.email,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Email'}
        />
      )
    },
  },
  {
    headerName: 'Support Name',
    field: 'support/name',
    width: 145,
    valueGetter: (_value, row) => row.support.name,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Name'}
        />
      )
    },
  },
  {
    headerName: 'Support Phone',
    field: 'support/phone',
    width: 145,
    valueGetter: (_value, row) => row.support.phone,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Phone'}
        />
      )
    },
  },
  {
    headerName: 'Support Email',
    field: 'support/email',
    width: 145,
    valueGetter: (_value, row) => row.support.email,
    renderHeader(params) {
      return (
        <GridColumnHeaderTitle
          columnWidth={params.colDef.width ?? 0}
          label={'Email'}
        />
      )
    },
  },
]

export const searchResultColumns: SearchColDef[] = [
  {
    field: '__check__',
    hideable: false,
    sortable: false,
    width: 50,
    headerAlign: 'left',
  },
  {
    headerName: 'Thumbnail',
    type: 'custom',
    field: 'thumbnail',
    width: 100,
    hideable: true,
    sortable: false,
    renderCell: ThumbnailCell,
  },
  {
    headerName: 'Media Number',
    field: 'mediaNumber',
    width: 200,
    hideable: false,
    renderCell: BulkEditIndicatorCell,
  },
  { headerName: 'Title', field: 'title', width: 500 },
  {
    headerName: 'Secure Date',
    field: 'secureDate',
    type: 'date',
    width: 100,
    align: 'right',
    valueGetter: (value) => new Date(value),
    valueFormatter: formatDate,
  },
  {
    headerName: 'Modified Date',
    field: 'dateModified',
    type: 'date',
    width: 115,
    align: 'right',
    valueGetter: (value) => new Date(value),
    valueFormatter: formatDate,
  },
  {
    headerName: 'Media Type',
    field: 'mediaType',
    width: 100,
    valueFormatter: (value: string) => {
      switch (value) {
        case 'D':
          return 'Document'
        case 'I':
          return 'Image'
        case 'V':
          return 'Video'
        default:
          return value
      }
    },
  },
  {
    headerName: 'Engine Models',
    field: 'engineModels',
    width: 120,
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Engine Markets',
    field: 'engineMarkets',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Categories',
    field: 'categories',
    width: 200,
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Product Types',
    field: 'productTypes',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Regions',
    field: 'regions',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Duties',
    field: 'duties',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Keywords',
    field: 'keywords',
    valueFormatter: (value: string[]) => (value ? value.join(', ') : ''),
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Language',
    field: 'languages',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  { headerName: 'Long Description', field: 'longDescription' },
  { headerName: 'Restrictions', field: 'restrictions' },
  {
    headerName: 'Status',
    field: 'status',
    width: 100,
    valueFormatter: (value: string) => {
      switch (value) {
        case 'C':
          return 'Current'
        case 'N':
          return 'Non-Current'
        default:
          return value
      }
    },
    renderCell: (params) => {
      let color = 'inherit'
      if (params.value === 'N') {
        color = 'error'
      }
      return <StatusCell color={color} {...params} />
    },
  }, // C Current NC Non-current
  {
    headerName: 'Internal Use Only',
    field: 'internalUseOnly',
    renderCell: CheckboxCell,
    width: 140,
  },
  {
    headerName: 'Has Clipping Art',
    field: 'clipPath',
    width: 135,
    renderCell: CheckboxCell,
  },
  {
    headerName: 'Performance Numbers',
    field: 'performanceRevisionNumbers',
    width: 175,
    valueGetter: getPerformanceAndRevisionNumber,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Sources',
    field: 'sources',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Fuel Types',
    field: 'fuelTypes',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Frequencies',
    field: 'frequencies',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    align: 'right',
    sortable: false,
  }, //array
  {
    headerName: 'RPMs',
    field: 'rpms',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    align: 'right',
    sortable: false,
  }, //array
  {
    headerName: 'Voltages',
    field: 'voltages',
    valueGetter: getPropertyItemDescription,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'kVA',
    field: 'ratings.kva',
    sortable: false,
    type: 'number',
    valueGetter: (_value, row) => row.ratings.kva,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
  },
  {
    headerName: 'bKW',
    field: 'ratings.bkw',
    sortable: false,
    type: 'number',

    valueGetter: (_value, row) => row.ratings.bkw,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
  },
  {
    headerName: 'bhp',
    field: 'ratings.bhp',
    type: 'number',
    sortable: false,

    valueGetter: (_value, row) => row.ratings.bhp,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
  },
  {
    headerName: 'ekW',
    field: 'ratings.ekw',
    type: 'number',
    sortable: false,

    valueGetter: (_value, row) => row.ratings.ekw,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
  },
  {
    headerName: 'mhp',
    field: 'ratings.mhp',
    sortable: false,
    type: 'number',

    valueGetter: (_value, row) => row.ratings.mhp,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
  },
  {
    headerName: 'Affiliations',
    field: 'affiliations',
    valueGetter: getAffCodeLabel,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Product ID',
    field: 'productIds',
    valueGetter: getPropertyItemDescription,
    valueFormatter: formatStringArray,
    renderCell: ExpandableCell,
    sortable: false,
  }, //array
  {
    headerName: 'Country of Use',
    field: 'fui',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //x meta data ask clay FUI?
  {
    headerName: 'Excitation',
    field: 'excit',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta EXCIT
  {
    headerName: 'Generator Arrangement',
    field: 'genarg',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta GENARG
  {
    headerName: 'Media Applies To',
    field: 'applyTo',
    valueGetter: getXMetadata,
    renderCell: ExpandableCell,
    sortable: false,
  }, //xmeta APPLYTO
] as const

export const columnGroups: GridColumnGroupingModel = [
  {
    groupId: 'ratings',
    headerName: 'Ratings',
    children: [
      { field: 'ratings.kva' },
      { field: 'ratings.bkw' },
      { field: 'ratings.bhp' },
      { field: 'ratings.ekw' },
      { field: 'ratings.mhp' },
    ],
  },
  {
    groupId: 'support',
    headerName: 'Support',
    children: [
      { field: 'support/name' },
      { field: 'support/phone' },
      { field: 'support/email' },
    ],
  },
  {
    groupId: 'provider',
    headerName: 'Provider',
    children: [
      { field: 'provider/name' },
      { field: 'provider/phone' },
      { field: 'provider/email' },
    ],
  },
]

function setDefaults(col: GridColDef): GridColDef {
  if (!col.width) {
    col.width = 150
    if (col.renderCell === ExpandableCell) col.width = 200
  }
  if (col.pinnable === undefined) {
    col.pinnable = false
  }
  return col satisfies GridColDef
}

export const useColumns = () => {
  const platform = useAppSelector(mediaSelectors.selectPlatform)
  const {
    selectors: { selectUserRights },
  } = useUser()

  const userRights = selectUserRights()

  const columns = useMemo(() => {
    let columns: GridColDef[] = []
    columns = columns.concat(actionColumn, searchResultColumns)
    if (platform === 'Cat') columns = columns.concat(catOnlyColumns)
    if (userRights.canViewExtraSearchDetails)
      columns = columns.concat(providerRoleColumns)

    return columns.map(setDefaults)
  }, [platform, userRights.canViewExtraSearchDetails])

  return columns
}
